<template>
  <div>
    <div class="cropper-wrapper">
      <img ref="image" :src="originalImageUrl" @load="initializeCropper">
    </div>
    <loading v-if="loading" class="white-background" />
  </div>
</template>

<script>
import Cropper from 'cropperjs'
import Loading from '@/components/loading'
import { nextTick } from 'vue'

import 'cropperjs/dist/cropper.css'

export default {
  components: { Loading },

  props: {
    aspectRatio: {
      type: Number,
      required: true
    },

    enforceAspectRatio: {
      type: Boolean,
      required: false,
      default: false
    },

    initialCropArea: {
      type: Object,
      required: false,
      default: null
    },

    originalImageUrl: {
      type: String,
      required: true
    }
  },

  emits: ['update'],

  data() {
    const initialCropParameters = {}

    if (this.enforceAspectRatio) {
      initialCropParameters.aspectRatio = this.aspectRatio
    }

    if (this.initialCropArea) {
      // Set the initial crop rectangle based on the prop.
      const { x, y, width, height } = this.initialCropArea
      initialCropParameters.data = { x, y, width, height }
    } else {
      // Set the initial crop rectangle to a rectangle with the right aspect ratio,
      // starting at the upper left corner, taking up either the full width or the full height
      // of the image, depending on the image's dimensions.
      initialCropParameters.initialAspectRatio = this.aspectRatio
      initialCropParameters.autoCropArea = 1.0
      initialCropParameters.data = {x: 0, y: 0}
    }

    return {
      cropper: null,
      initialCropParameters,
      loading: true
    }
  },

  beforeUnmount() {
    this.cropper.destroy()
  },

  methods: {
    cropUpdated() {
      const { x, y, height, width } = this.cropper.getData()
      this.$emit('update', { x, y, height, width })
    },

    finishedLoading() {
      this.loading = false
    },

    initializeCropper() {
      nextTick(() => {
        const imgElement = this.$refs.image
        const component = this

        // Options documentation: https://github.com/fengyuanchen/cropperjs/blob/main/README.md#options
        this.cropper = new Cropper(imgElement, {
          ...this.initialCropParameters,
          viewMode: 3,
          movable: false,
          rotatable: false,
          scalable: false,
          zoomable: false,

          ready() {
            // Let the component know we've finished loading the cropper
            component.finishedLoading()

            // Emit the initial crop rectangle, so a user can save the initial crop if desired
            component.cropUpdated()
          },

          // This is called when the user finishes drawing or moving the cropping rectangle
          cropend() {
            component.cropUpdated()
          }
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.cropper-wrapper {
  ::v-deep(img) {
    display: block;
    width: 100%;
  }
}
</style>
